import React, { useState } from 'react';

// Mock data for assignments
const mockAssignments = [
  { id: 1, title: 'Essay on Climate Change', dueDate: '2025-04-15', submissions: 18, totalStudents: 25 },
  { id: 2, title: 'Programming Project: Data Analysis', dueDate: '2025-04-22', submissions: 12, totalStudents: 25 },
  { id: 3, title: 'Research Report: Renewable Energy', dueDate: '2025-05-01', submissions: 5, totalStudents: 25 },
];

// Mock data for submissions
const mockSubmissions = [
  { id: 1, studentName: 'Alex Johnson', assignmentId: 1, submissionDate: '2025-04-10', status: 'Reviewed' },
  { id: 2, studentName: 'Jamie Smith', assignmentId: 1, submissionDate: '2025-04-11', status: 'Pending' },
  { id: 3, studentName: 'Taylor Brown', assignmentId: 2, submissionDate: '2025-04-15', status: 'In Progress' },
  { id: 4, studentName: 'Morgan Lee', assignmentId: 1, submissionDate: '2025-04-09', status: 'Reviewed' },
  { id: 5, studentName: 'Casey Wilson', assignmentId: 3, submissionDate: '2025-04-20', status: 'Pending' },
];

const InstructorDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('assignments');
  const [showRubricBuilder, setShowRubricBuilder] = useState(false);
  const [rubricCriteria, setRubricCriteria] = useState([
    { id: 1, name: 'Content', description: 'Depth and relevance of content', weight: 30 },
    { id: 2, name: 'Structure', description: 'Organization and flow of ideas', weight: 25 },
    { id: 3, name: 'Evidence', description: 'Use of supporting evidence and examples', weight: 25 },
    { id: 4, name: 'Language', description: 'Grammar, spelling, and style', weight: 20 },
  ]);

  const addRubricCriteria = () => {
    const newId = rubricCriteria.length > 0 ? Math.max(...rubricCriteria.map(c => c.id)) + 1 : 1;
    setRubricCriteria([...rubricCriteria, { id: newId, name: '', description: '', weight: 0 }]);
  };

  const updateRubricCriteria = (id: number, field: string, value: string | number) => {
    setRubricCriteria(
      rubricCriteria.map(criteria => 
        criteria.id === id ? { ...criteria, [field]: value } : criteria
      )
    );
  };

  const removeRubricCriteria = (id: number) => {
    setRubricCriteria(rubricCriteria.filter(criteria => criteria.id !== id));
  };

  return (
    <div className="bg-neutral min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-primary">Instructor Dashboard</h1>
            <p className="text-gray-600 mt-2">Manage assignments, rubrics, and student feedback</p>
          </div>
          <div className="mt-4 md:mt-0">
            <button 
              onClick={() => setShowRubricBuilder(!showRubricBuilder)}
              className="btn btn-primary"
            >
              {showRubricBuilder ? 'Close Rubric Builder' : 'Create New Assignment'}
            </button>
          </div>
        </div>

        {/* Rubric Builder */}
        {showRubricBuilder && (
          <div className="card mb-8">
            <h2 className="text-xl font-bold text-primary mb-4">Create New Assignment</h2>
            
            <div className="mb-6">
              <label htmlFor="assignmentTitle" className="block text-sm font-medium text-gray-700 mb-1">
                Assignment Title
              </label>
              <input
                type="text"
                id="assignmentTitle"
                className="input w-full"
                placeholder="e.g., Essay on Climate Change"
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="assignmentDescription" className="block text-sm font-medium text-gray-700 mb-1">
                Assignment Description
              </label>
              <textarea
                id="assignmentDescription"
                rows={3}
                className="input w-full"
                placeholder="Provide detailed instructions for students..."
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <input
                type="date"
                id="dueDate"
                className="input w-full"
              />
            </div>
            
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-medium text-primary">Rubric Criteria</h3>
                <button 
                  onClick={addRubricCriteria}
                  className="text-sm text-primary hover:text-opacity-80 flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Add Criteria
                </button>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                {rubricCriteria.map((criteria) => (
                  <div key={criteria.id} className="grid grid-cols-12 gap-4 mb-4 items-start">
                    <div className="col-span-3">
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Criteria Name
                      </label>
                      <input
                        type="text"
                        value={criteria.name}
                        onChange={(e) => updateRubricCriteria(criteria.id, 'name', e.target.value)}
                        className="input w-full"
                        placeholder="e.g., Content"
                      />
                    </div>
                    <div className="col-span-6">
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={criteria.description}
                        onChange={(e) => updateRubricCriteria(criteria.id, 'description', e.target.value)}
                        className="input w-full"
                        placeholder="What this criteria evaluates..."
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Weight (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={criteria.weight}
                        onChange={(e) => updateRubricCriteria(criteria.id, 'weight', parseInt(e.target.value) || 0)}
                        className="input w-full"
                      />
                    </div>
                    <div className="col-span-1 pt-6">
                      <button 
                        onClick={() => removeRubricCriteria(criteria.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end">
              <button className="btn bg-gray-200 text-gray-800 hover:bg-gray-300 mr-4">
                Cancel
              </button>
              <button className="btn btn-primary">
                Create Assignment
              </button>
            </div>
          </div>
        )}

        {/* Dashboard Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('assignments')}
              className={`${
                activeTab === 'assignments'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Assignments
            </button>
            <button
              onClick={() => setActiveTab('submissions')}
              className={`${
                activeTab === 'submissions'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Submissions
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`${
                activeTab === 'analytics'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Analytics
            </button>
          </nav>
        </div>

        {/* Assignments Tab */}
        {activeTab === 'assignments' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assignment
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submissions
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mockAssignments.map((assignment) => (
                  <tr key={assignment.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{assignment.title}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{assignment.dueDate}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {assignment.submissions} / {assignment.totalStudents}
                        <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                          <div 
                            className="bg-accent-2 h-2.5 rounded-full" 
                            style={{ width: `${(assignment.submissions / assignment.totalStudents) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button className="text-primary hover:text-opacity-80 mr-4">Edit</button>
                      <button className="text-primary hover:text-opacity-80">View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Submissions Tab */}
        {activeTab === 'submissions' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <div className="flex flex-col md:flex-row justify-between">
                <div className="mb-4 md:mb-0">
                  <label htmlFor="filterAssignment" className="block text-sm font-medium text-gray-700 mb-1">
                    Filter by Assignment
                  </label>
                  <select id="filterAssignment" className="input">
                    <option value="">All Assignments</option>
                    {mockAssignments.map(assignment => (
                      <option key={assignment.id} value={assignment.id}>{assignment.title}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="filterStatus" className="block text-sm font-medium text-gray-700 mb-1">
                    Filter by Status
                  </label>
                  <select id="filterStatus" className="input">
                    <option value="">All Statuses</option>
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Reviewed">Reviewed</option>
                  </select>
                </div>
              </div>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assignment
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mockSubmissions.map((submission) => (
                  <tr key={submission.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{submission.studentName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {mockAssignments.find(a => a.id === submission.assignmentId)?.title}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{submission.submissionDate}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${submission.status === 'Reviewed' ? 'bg-green-100 text-green-800' : 
                          submission.status === 'In Progress' ? 'bg-blue-100 text-blue-800' : 
                          'bg-yellow-100 text-yellow-800'}`}>
                        {submission.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button className="text-primary hover:text-opacity-80">Review</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-primary mb-6">Performance Analytics</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-neutral p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-1">Total Assignments</h3>
                <p className="text-3xl font-bold text-primary">{mockAssignments.length}</p>
              </div>
              <div className="bg-neutral p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-1">Total Submissions</h3>
                <p className="text-3xl font-bold text-primary">{mockSubmissions.length}</p>
              </div>
              <div className="bg-neutral p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-1">Feedback Completion</h3>
                <p className="text-3xl font-bold text-primary">
                  {Math.round((mockSubmissions.filter(s => s.status === 'Reviewed').length / mockSubmissions.length) * 100)}%
                </p>
              </div>
            </div>
            
            <div className="mb-8">
              <h3 className="text-lg font-medium text-primary mb-4">Submission Timeline</h3>
              <div className="h-64 bg-neutral rounded-lg flex items-center justify-center">
                <p className="text-gray-500">Submission timeline visualization would appear here</p>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-primary mb-4">Common Feedback Themes</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-neutral p-4 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2">Areas of Strength</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <div className="h-4 w-4 rounded-full bg-green-500 mr-2"></div>
                      <span className="text-sm">Critical thinking (78%)</span>
                    </li>
                    <li className="flex items-center">
                      <div className="h-4 w-4 rounded-full bg-green-500 mr-2"></div>
                      <span className="text-sm">Research depth (65%)</span>
                    </li>
                    <li className="flex items-center">
                      <div className="h-4 w-4 rounded-full bg-green-500 mr-2"></div>
                      <span className="text-sm">Creativity (62%)</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-neutral p-4 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2">Areas for Improvement</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <div className="h-4 w-4 rounded-full bg-red-500 mr-2"></div>
                      <span className="text-sm">Citation format (45%)</span>
                    </li>
                    <li className="flex items-center">
                      <div className="h-4 w-4 rounded-full bg-red-500 mr-2"></div>
                      <span className="text-sm">Argument structure (42%)</span>
                    </li>
                    <li className="flex items-center">
                      <div className="h-4 w-4 rounded-full bg-red-500 mr-2"></div>
                      <span className="text-sm">Grammar and syntax (38%)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstructorDashboard;
